import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import Homepage from "./pages/Homepage";
import PublisherApplicationPage from "./pages/PublisherApplicationPage";
import OfferApplicationPage from "./pages/OfferApplicationPage";
import BuyerApplicationPage from "./pages/BuyerApplicationPage";
import AboutPage from "./pages/AboutPage";
import CallIntellPage from "./pages/CallIntellPage";
import DemoLogin from "./publisher-demo/DemoLogin";
import DemoPanel from "./publisher-demo/DemoPanel";
import OffersPage from "./pages/OffersPage";
import ContactPage from "./pages/ContactPage";
import FAQPage from "./pages/FAQPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CareersPage from "./pages/CareersPage";
import LearnPage from "./pages/LearnPage";
import CourseApplicationPage from "./pages/CourseApplicationPage";
import JobApplicationPage from "./pages/JobApplicationPage";
import BlogPage from "./pages/BlogPage";
import AdCreativesPage from "./pages/AdCreativesPage";
import LeadsGenerationPage from "./pages/LeadsGenerationPage";
import AdminPublishersPage from "./pages/AdminPublishersPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminCampaignsPage from "./pages/AdminCampaignsPage";
import AdminPaymentsPage from "./pages/AdminPaymentsPage";
import AdminOffersPage from "./pages/AdminOffersPage";
import AdminOfferRequestsPage from "./pages/AdminOfferRequestsPage";
import AdminSecurityPage from "./pages/AdminSecurityPage";
import AdminBlacklistPage from "./pages/AdminBlacklistPage";
import AdminAnalyticsPage from "./pages/AdminAnalyticsPage";
import AdminZipManagerPage from "./pages/AdminZipManagerPage";
import TargetZipsPage from "./pages/TargetZipsPage";
import DesignLabPage from "./pages/DesignLabPage";
import ThemeDemoPage from "./pages/ThemeDemoPage";
import WebsiteDevelopmentPage from "./pages/WebsiteDevelopmentPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import MobileSignupDock from "./components/MobileSignupDock";
import { trackPageview } from "./utils/analyticsTracker";
import { captureFirstTouch } from "./utils/referrerCapture";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    // Fire a pageview on every route change (skips /admin/* internally)
    trackPageview(pathname);
    // Persist first-touch referrer/landing on first ever visit (no-op after)
    captureFirstTouch();
  }, [pathname]);
  
  return null;
}

// Mobile-only Publisher / Buyer signup dock — hidden on admin routes and on
// pages that already contain a primary form (so the dock never blocks the
// submit button or duplicates the CTA).
// We match on prefix ("/apply" also hides on "/apply/google_ads" etc.) so
// every source-specific form page stays focused for mobile users.
const FORM_ROUTE_PREFIXES = [
  "/apply",
  "/buyer",
  "/adcreatives",
  "/contact",
  "/course-apply",
  "/job-apply",
  "/leads-generation",
];

function MobileSignupDockGate() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin") || pathname.startsWith("/publisher-demo")) return null;
  if (FORM_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
    return null;
  }
  return <MobileSignupDock />;
}

function App() {
  // Remove any external branding badges
  useEffect(() => {
    const removeBranding = () => {
      const selectors = [
        'a[href*="emergentagent.com"]:not([href*="preview"]):not([href*="customer-assets"])',
        '[class*="emergent" i]',
        '[id*="emergent" i]'
      ];
      selectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          if (el && el.parentNode && !el.href?.includes('preview') && !el.href?.includes('customer-assets')) {
            el.style.display = 'none';
            el.remove();
          }
        });
      });
    };
    
    // Run immediately and observe for dynamic additions
    removeBranding();
    const observer = new MutationObserver(removeBranding);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route path="/apply" element={<PublisherApplicationPage />} />
          <Route path="/apply/:source" element={<PublisherApplicationPage />} />
          <Route path="/apply-new-offer/:publisherId" element={<OfferApplicationPage />} />
          {/* Marketing alias — redirects to /apply, preserving ?source= params */}
          <Route
            path="/publisher-signup"
            element={<Navigate to={`/apply${window.location.search}`} replace />}
          />
          <Route path="/buyer" element={<BuyerApplicationPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/offers" element={<OffersPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/learn" element={<LearnPage />} />
          <Route path="/course-apply" element={<CourseApplicationPage />} />
          <Route path="/job-apply" element={<JobApplicationPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/adcreatives" element={<AdCreativesPage />} />
          <Route path="/leads-generation" element={<LeadsGenerationPage />} />
          <Route path="/target-zips" element={<TargetZipsPage />} />
          <Route path="/design-lab" element={<DesignLabPage />} />
          <Route path="/theme-demo" element={<ThemeDemoPage />} />
          <Route path="/website-development" element={<WebsiteDevelopmentPage />} />
          <Route path="/callintell" element={<CallIntellPage />} />
          <Route path="/publisher-demo" element={<DemoLogin />} />
          <Route path="/publisher-demo/panel" element={<DemoPanel />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          <Route path="/offervault" element={<Navigate to="/apply?source=OfferVault" replace />} />
          <Route path="/bid-coverage" element={<Navigate to="/" replace />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin/publishers"
            element={
              <AdminProtectedRoute>
                <AdminPublishersPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/campaigns"
            element={
              <AdminProtectedRoute>
                <AdminCampaignsPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/payments"
            element={
              <AdminProtectedRoute>
                <AdminPaymentsPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/offer-requests"
            element={
              <AdminProtectedRoute>
                <AdminOfferRequestsPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/offers"
            element={
              <AdminProtectedRoute>
                <AdminOffersPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/security"
            element={
              <AdminProtectedRoute>
                <AdminSecurityPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/blacklist"
            element={
              <AdminProtectedRoute>
                <AdminBlacklistPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <AdminProtectedRoute>
                <AdminAnalyticsPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/zip-manager"
            element={
              <AdminProtectedRoute>
                <AdminZipManagerPage />
              </AdminProtectedRoute>
            }
          />
        </Routes>
        <MobileSignupDockGate />
      </BrowserRouter>
    </div>
  );
}

export default App;
